import { Card, CardContent, Input, Button } from '@lensora/ui'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X, Trash2, Globe, Crown, Eye, AlertTriangle, Lock, ChevronRight, Loader2 } from 'lucide-react'
import * as React from 'react'
import { Link } from 'react-router-dom'

import { CompetitorDrawer, type DrawerCompetitor } from '@/components/dashboard/CompetitorDrawer'
import { CompetitorScatterPlot } from '@/components/dashboard/CompetitorScatterPlot'
import { FaviconImg } from '@/components/dashboard/FaviconImg'
import { PlatformIcon, formatPlatformName, PICKER_PLATFORMS } from '@/components/dashboard/PlatformIcon'
import { useActiveWebsite } from '@/hooks/use-active-website'
import { api } from '@/lib/api'

// ── Types ──────────────────────────────────────────────────────────────────────

interface LeaderboardEntry {
  rank: number
  competitor_id: string | null
  name: string
  domain: string
  favicon_url: string
  mention_rate: number
  avg_position: number | null
  sentiment_score: number
  overall_score: number
  mention_rate_delta: number | null
  sentiment_delta: number | null
  position_delta: number | null
  scan_count: number
  total_scan_count: number
  is_own_brand: boolean
  is_brand_placeholder: boolean
  platform_stats?: Record<string, { mentioned: number; total: number }>
  prompt_coverage?: number
  mention_count?: number
}

interface GapResult {
  query_text: string
  competitor_name: string | null
  mention_position: number | null
}

interface AiDiscoveredBrand {
  id: string
  name: string
  category: string | null
  times_seen: number
}

// ── Helpers ────────────────────────────────────────────────────────────────────

// ── Period tab strip ───────────────────────────────────────────────────────────

const PERIOD_TABS = [
  { label: '7D', value: '7d' },
  { label: '30D', value: '30d' },
  { label: '90D', value: '90d' },
  { label: 'All', value: 'all' },
]

const VISIBLE_PLATFORMS = PICKER_PLATFORMS

// ── Delta indicator ────────────────────────────────────────────────────────────

function Delta({ delta }: { delta: number | null }) {
  if (delta === null || delta === 0) return null
  const up = delta > 0
  return (
    <span className={`ml-1 shrink-0 text-[11px] font-semibold ${up ? 'text-feedback-success' : 'text-feedback-error'}`}>
      {up ? '↗' : '↘'} {Math.abs(delta)}
    </span>
  )
}

// ── Sentiment colour ───────────────────────────────────────────────────────────

function sentimentColor(score: number) {
  if (score >= 70) return '#16a34a'
  if (score >= 40) return '#ca8a04'
  return '#dc2626'
}

// ── Leaderboard ────────────────────────────────────────────────────────────────

function LeaderboardSection({
  leaderboard,
  loading,
  removing,
  period,
  onPeriodChange,
  onRemove,
  onRowClick,
}: {
  leaderboard: LeaderboardEntry[]
  loading: boolean
  removing: string | null
  period: string
  onPeriodChange: (p: string) => void
  onRemove: (id: string) => void
  onRowClick: (entry: LeaderboardEntry) => void
}) {
  if (loading) {
    return (
      <div className="overflow-hidden rounded-lg border border-border bg-surface">
        <div className="border-b border-border px-6 py-4">
          <div className="mb-2 h-4 w-44 animate-pulse rounded bg-surface-overlay" />
          <div className="flex gap-3">
            {PERIOD_TABS.map((t) => <div key={t.value} className="h-3 w-8 animate-pulse rounded bg-surface-overlay" />)}
          </div>
        </div>
        {[1, 2, 3, 4].map((i) => <div key={i} className="mx-4 my-2 h-14 animate-pulse rounded bg-surface-overlay" />)}
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <h2 className="text-sm font-semibold text-text">AI Visibility Rankings</h2>
        <div className="flex overflow-hidden rounded-lg border border-border">
          {PERIOD_TABS.map((tab, i) => (
            <button
              key={tab.value}
              onClick={() => onPeriodChange(tab.value)}
              className={`px-3 py-1.5 text-xs transition-colors ${
                period === tab.value
                  ? 'bg-brand-accent/10 font-semibold text-brand-accent'
                  : 'text-text-muted hover:bg-surface-overlay'
              } ${i > 0 ? 'border-l border-border' : ''}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {leaderboard.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Crown className="mb-3 h-8 w-8 text-text-muted opacity-40" />
          <p className="text-sm text-text-muted">Add competitors to see how you rank against them</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-raised">
                {['#', 'Brand', 'Overall %', ...VISIBLE_PLATFORMS.map((p) => formatPlatformName(p)), 'Sentiment', 'Avg Rank', ''].map((h, i) => (
                  <th
                    key={i}
                    className="px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-text-muted"
                    style={{ width: i === 0 ? 44 : i === 1 ? 220 : i >= 3 && i <= 6 ? 72 : 100 }}
                  >
                    {VISIBLE_PLATFORMS[i - 3] ? (
                      <span title={h} className="flex items-center">
                        <PlatformIcon platform={VISIBLE_PLATFORMS[i - 3]!} size={14} />
                      </span>
                    ) : h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {leaderboard.map((entry) => {
                const noData = entry.scan_count === 0
                const isOwn = entry.is_own_brand
                const isClickable = !isOwn && !!entry.competitor_id

                return (
                  <tr
                    key={entry.domain}
                    className={`cursor-${isClickable ? 'pointer' : 'default'} transition-colors hover:bg-surface-overlay ${
                      isOwn ? 'bg-brand-accent/5 shadow-[inset_3px_0_0_#6366f1]' : ''
                    }`}
                    onClick={isClickable ? () => onRowClick(entry) : undefined}
                  >
                    {/* Rank */}
                    <td className="px-4 py-3.5">
                      <span className="text-xs text-text-muted">{entry.rank}</span>
                    </td>

                    {/* Brand */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2.5 min-w-0">
                        {entry.is_brand_placeholder ? (
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-accent/10 text-xs font-bold text-brand-accent">?</div>
                        ) : (
                          <FaviconImg domain={entry.domain} name={entry.name} size={32} />
                        )}
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="max-w-[140px] truncate text-sm font-medium text-text">{entry.name}</span>
                            {isOwn && <span className="text-[10px] text-text-muted">you</span>}
                          </div>
                          {!isOwn && entry.domain && (
                            <span className="max-w-[140px] truncate text-xs text-text-muted">{entry.domain}</span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Overall % */}
                    <td className="px-4 py-3.5">
                      {noData ? (
                        <span className="text-xs text-text-muted">—</span>
                      ) : (
                        <div className="flex items-center">
                          <span className="text-sm font-semibold text-brand-accent">
                            {entry.prompt_coverage ?? entry.mention_rate}%
                          </span>
                          <Delta delta={entry.mention_rate_delta} />
                        </div>
                      )}
                    </td>

                    {/* Per-platform */}
                    {VISIBLE_PLATFORMS.map((p) => {
                      const stat = entry.platform_stats?.[p]
                      const hasData = !noData && stat && stat.total > 0
                      const pct = hasData ? Math.round((stat!.mentioned / stat!.total) * 100) : null
                      return (
                        <td key={p} className="px-4 py-3.5">
                          {!hasData ? (
                            <span className="text-xs text-text-muted">—</span>
                          ) : (
                            <span className="text-sm font-medium" style={{ color: pct! >= 50 ? '#16a34a' : pct! >= 20 ? '#ca8a04' : '#64748b' }}>
                              {pct}%
                            </span>
                          )}
                        </td>
                      )
                    })}

                    {/* Sentiment */}
                    <td className="px-4 py-3.5">
                      {noData || entry.sentiment_score === null ? (
                        <span className="text-xs text-text-muted">—</span>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <div
                            className="h-3 w-0.5 shrink-0 rounded-full"
                            style={{ backgroundColor: sentimentColor(entry.sentiment_score) }}
                          />
                          <span className="text-sm font-medium text-text">{entry.sentiment_score}</span>
                          <Delta delta={entry.sentiment_delta} />
                        </div>
                      )}
                    </td>

                    {/* Avg rank */}
                    <td className="px-4 py-3.5">
                      {noData || entry.avg_position === null ? (
                        <span className="text-xs text-text-muted">—</span>
                      ) : (
                        <div className="flex items-center gap-0.5">
                          <span className="text-[11px] text-text-muted">#</span>
                          <span
                            className="text-sm font-medium"
                            style={{
                              color: entry.avg_position <= 3 ? '#16a34a'
                                : entry.avg_position <= 6 ? '#ca8a04'
                                : '#64748b'
                            }}
                          >
                            {entry.avg_position}
                          </span>
                          <Delta delta={entry.position_delta} />
                        </div>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-2 py-3.5">
                      <div className="flex items-center gap-1">
                        {isClickable && <ChevronRight className="h-3.5 w-3.5 text-text-muted" />}
                        {entry.competitor_id && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onRemove(entry.competitor_id!) }}
                            disabled={removing === entry.competitor_id}
                            className="rounded p-1 text-text-muted transition-colors hover:bg-feedback-error/10 hover:text-feedback-error disabled:opacity-40"
                            title={`Remove ${entry.name}`}
                          >
                            {removing === entry.competitor_id
                              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              : <Trash2 className="h-3.5 w-3.5" />
                            }
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export function CompetitorsPage() {
  const queryClient = useQueryClient()

  const [name, setName] = React.useState('')
  const [url, setUrl] = React.useState('')
  const [addError, setAddError] = React.useState('')
  const [limitReached, setLimitReached] = React.useState(false)
  const [removing, setRemoving] = React.useState<string | null>(null)
  const [period, setPeriod] = React.useState('7d')
  const [drawerCompetitor, setDrawerCompetitor] = React.useState<DrawerCompetitor | null>(null)
  const [trackingBrand, setTrackingBrand] = React.useState<string | null>(null)
  const [dismissingBrand, setDismissingBrand] = React.useState<string | null>(null)

  // ── Queries ──────────────────────────────────────────────────────────────────

  const { websiteId, isLoading: websitesLoading } = useActiveWebsite()

  const { data: leaderboardData, isLoading: leaderboardLoading } = useQuery({
    queryKey: ['leaderboard', websiteId, period],
    queryFn: () => api.get<{ leaderboard: LeaderboardEntry[] }>(`/api/competitors/leaderboard?website_id=${websiteId}&period=${period}`),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  const { data: gapsData, isLoading: gapsLoading } = useQuery({
    queryKey: ['competitor-gaps', websiteId],
    queryFn: () => api.get<{ gaps: GapResult[] }>(`/api/competitors/gaps?website_id=${websiteId}`),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  const { data: aiData, isLoading: aiLoading } = useQuery({
    queryKey: ['ai-suggestions', websiteId],
    queryFn: () => api.get<{ suggestions: AiDiscoveredBrand[] }>(`/api/competitors/ai-suggestions?website_id=${websiteId}`),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  const leaderboard = leaderboardData?.leaderboard ?? []
  const gaps = gapsData?.gaps ?? []
  const aiDiscovered = (aiData?.suggestions ?? []).map((s) => ({
    ...s,
    name: s.name.replace(/^\[|\]$/g, '').trim(),
  }))

  // ── Mutations ────────────────────────────────────────────────────────────────

  const addMutation = useMutation({
    mutationFn: (body: { websiteId: string; url: string; name: string }) =>
      api.post<{ competitor: { id: string; name: string | null } }>('/api/competitors', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaderboard', websiteId] })
      queryClient.invalidateQueries({ queryKey: ['ai-suggestions', websiteId] })
      setName('')
      setUrl('')
      setAddError('')
    },
  })

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/competitors?id=${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaderboard', websiteId] })
    },
  })

  const trackAiBrandMutation = useMutation({
    mutationFn: (brand: AiDiscoveredBrand) => {
      const domain = brand.name.toLowerCase().replace(/[^a-z0-9.]/g, '') + (brand.name.includes('.') ? '' : '.com')
      return api.post('/api/competitors', {
        websiteId: websiteId,
        url: `https://${domain}`,
        name: brand.name,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaderboard', websiteId] })
      queryClient.invalidateQueries({ queryKey: ['ai-suggestions', websiteId] })
    },
  })

  const dismissAiBrandMutation = useMutation({
    mutationFn: (name: string) =>
      api.patch('/api/competitors/ai-suggestions', { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai-suggestions', websiteId] })
    },
  })

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleAdd = async () => {
    setAddError('')
    setLimitReached(false)
    if (!name.trim()) { setAddError('Please enter a competitor name'); return }
    if (!url.trim())  { setAddError('Please enter a website URL'); return }
    if (!websiteId)   { setAddError('No website found — add your website in Settings first'); return }

    const fullUrl = url.trim().startsWith('http') ? url.trim() : `https://${url.trim()}`
    try {
      await addMutation.mutateAsync({ websiteId: websiteId, url: fullUrl, name: name.trim() })
    } catch (err: unknown) {
      const e = err as { limit_reached?: boolean; message?: string }
      if (e.limit_reached) setLimitReached(true)
      setAddError(e.message ?? 'Failed to add competitor')
    }
  }

  const handleRemove = async (id: string) => {
    setRemoving(id)
    try {
      await removeMutation.mutateAsync(id)
    } catch (err: unknown) {
      const e = err as { message?: string }
      setAddError(e.message ?? 'Failed to remove competitor')
    } finally {
      setRemoving(null)
    }
  }

  const handleTrackAiBrand = async (brand: AiDiscoveredBrand) => {
    setTrackingBrand(brand.name)
    try {
      await trackAiBrandMutation.mutateAsync(brand)
    } catch (err: unknown) {
      const e = err as { limit_reached?: boolean; message?: string }
      if (e.limit_reached) setLimitReached(true)
    } finally {
      setTrackingBrand(null)
    }
  }

  const handleDismissAiBrand = async (brand: AiDiscoveredBrand) => {
    setDismissingBrand(brand.name)
    try {
      await dismissAiBrandMutation.mutateAsync(brand.name)
    } finally {
      setDismissingBrand(null)
    }
  }

  // ── Group gaps by prompt ──────────────────────────────────────────────────────

  const groupedGaps = React.useMemo(() => {
    const map = new Map<string, Array<{ name: string | null; position: number | null }>>()
    for (const gap of gaps) {
      if (!map.has(gap.query_text)) map.set(gap.query_text, [])
      map.get(gap.query_text)!.push({ name: gap.competitor_name, position: gap.mention_position })
    }
    return [...map.entries()]
      .map(([query_text, competitors]) => ({ query_text, competitors }))
      .sort((a, b) => b.competitors.length - a.competitors.length)
  }, [gaps])

  // ── Loading state ─────────────────────────────────────────────────────────────

  if (websitesLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-text-muted" />
      </div>
    )
  }

  // ── No website ────────────────────────────────────────────────────────────────

  if (!websiteId) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-bold text-text">Competitor Tracking</h2>
          <p className="mt-1 text-sm text-text-muted">Monitor your AI visibility vs competitors and find gaps to close</p>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Globe className="mb-4 h-10 w-10 text-text-muted opacity-40" />
            <p className="font-medium text-text">No website set up yet</p>
            <p className="mt-1 text-sm text-text-muted">Add your website in Settings before tracking competitors.</p>
            <Link
              to="/dashboard/settings"
              className="mt-4 text-sm font-semibold text-brand-accent hover:underline"
            >
              Go to Settings →
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  // ── Main layout ───────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h2 className="text-2xl font-bold text-text">Competitor Tracking</h2>
        <p className="mt-1 text-sm text-text-muted">Monitor your AI visibility vs competitors and find gaps to close</p>
      </div>

      {/* Two-column grid */}
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[65fr_35fr]">

        {/* Left column */}
        <div className="space-y-6">
          <LeaderboardSection
            leaderboard={leaderboard}
            loading={leaderboardLoading}
            removing={removing}
            period={period}
            onPeriodChange={(p) => { setPeriod(p) }}
            onRemove={handleRemove}
            onRowClick={(entry) => {
              if (!entry.competitor_id) return
              setDrawerCompetitor({
                competitor_id: entry.competitor_id,
                name: entry.name,
                domain: entry.domain,
                favicon_url: entry.favicon_url,
                mention_rate: entry.mention_rate,
                rank: entry.rank,
              })
            }}
          />

          <CompetitorScatterPlot leaderboard={leaderboard} loading={leaderboardLoading} />

          {/* Queries They're Winning */}
          <div className="rounded-lg border border-border bg-surface p-6">
            <h2 className="text-sm font-semibold text-text">Queries They're Winning</h2>
            <p className="mb-4 mt-0.5 text-xs text-text-muted">Prompts where competitors appear in AI responses but your brand doesn't</p>

            {gapsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-surface-overlay" />)}
              </div>
            ) : groupedGaps.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border bg-surface-raised py-10 text-center">
                <p className="text-sm text-text-muted">No gaps detected yet</p>
                <p className="mt-1 px-4 text-xs text-text-muted opacity-70">
                  Gaps appear when competitors are mentioned in AI responses but your brand isn't. Run more simulations to discover them.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {groupedGaps.map((group) => (
                  <div key={group.query_text} className="rounded-lg border border-border bg-surface-raised px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <p className="flex-1 text-sm leading-snug text-text">{group.query_text}</p>
                      <div className="flex shrink-0 flex-wrap items-center gap-1">
                        {group.competitors.filter((c) => c.name).map((c, i) => (
                          <span key={i} className="rounded-full bg-brand-accent/10 px-2 py-0.5 text-xs font-medium text-brand-accent">
                            {c.name}
                            {c.position !== null && <span className="ml-1 text-[10px] text-text-muted">#{c.position}</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                    <Link
                      to={`/dashboard/content-briefs?prompt=${encodeURIComponent(group.query_text)}`}
                      className="mt-2 flex items-center gap-1 self-start text-[10px] font-semibold text-brand-accent hover:underline"
                    >
                      Generate content to win this →
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right column (sticky) */}
        <div className="sticky top-6 flex flex-col gap-4">

          {/* AI Discovered */}
          {(aiLoading || aiDiscovered.length > 0) && (
            <div className="overflow-hidden rounded-lg border border-border bg-surface">
              <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                <div className="h-4 w-4 shrink-0 rounded bg-brand-accent/20 text-brand-accent" />
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-text">Discovered by AI</h2>
                  <p className="text-[10px] text-text-muted">Brands appearing alongside yours in AI responses</p>
                </div>
              </div>

              {aiLoading ? (
                <div className="divide-y divide-border">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-2 px-4 py-2">
                      <div className="h-4 w-4 shrink-0 animate-pulse rounded bg-surface-overlay" />
                      <div className="h-3 w-20 animate-pulse rounded bg-surface-overlay" />
                    </div>
                  ))}
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-4 py-2 text-left text-[9px] font-medium uppercase tracking-widest text-text-muted">Brand</th>
                      <th className="py-2 text-left text-[9px] font-medium uppercase tracking-widest text-text-muted">Category</th>
                      <th className="py-2 pr-3 text-right text-[9px] font-medium uppercase tracking-widest text-text-muted">Seen</th>
                      <th className="py-2 pr-4" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {aiDiscovered.map((brand) => {
                      const domain = brand.name.toLowerCase().replace(/[^a-z0-9.]/g, '') + (brand.name.includes('.') ? '' : '.com')
                      const isTracking = trackingBrand === brand.name
                      const isDismissing = dismissingBrand === brand.name
                      return (
                        <tr key={brand.name} className="transition-colors hover:bg-surface-overlay">
                          <td className="px-4 py-2">
                            <div className="flex min-w-0 items-center gap-2">
                              <FaviconImg domain={domain} name={brand.name} size={16} className="shrink-0 rounded" />
                              <span className="max-w-[100px] truncate text-xs font-medium text-text">{brand.name}</span>
                            </div>
                          </td>
                          <td className="py-2 pr-2">
                            {brand.category && (
                              <span className="whitespace-nowrap rounded bg-surface-overlay px-1.5 py-0.5 text-[10px] text-text-muted">
                                {brand.category}
                              </span>
                            )}
                          </td>
                          <td className="py-2 pr-3 text-right">
                            <span className="flex items-center justify-end gap-1 text-[10px] text-text-muted">
                              <Eye className="h-3 w-3" />{brand.times_seen}×
                            </span>
                          </td>
                          <td className="py-2 pr-4">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                onClick={() => handleTrackAiBrand(brand)}
                                disabled={isTracking || isDismissing}
                                title="Track competitor"
                                className="flex h-6 w-6 items-center justify-center rounded-md bg-brand-accent/10 transition-colors hover:bg-brand-accent/20 disabled:opacity-40"
                              >
                                {isTracking
                                  ? <Loader2 className="h-3 w-3 animate-spin text-brand-accent" />
                                  : <Plus className="h-3 w-3 text-brand-accent" />
                                }
                              </button>
                              <button
                                onClick={() => handleDismissAiBrand(brand)}
                                disabled={isTracking || isDismissing}
                                title="Dismiss"
                                className="flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-surface-overlay disabled:opacity-40"
                              >
                                {isDismissing
                                  ? <Loader2 className="h-3 w-3 animate-spin text-text-muted" />
                                  : <X className="h-3 w-3 text-text-muted" />
                                }
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Add Competitor Form */}
          <div className="rounded-lg border border-border bg-surface p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-text">Add Competitor</h2>
              <span className="text-xs text-text-muted">{leaderboard.filter((e) => !e.is_own_brand).length} tracked</span>
            </div>
            <div className="flex flex-col gap-2">
              <Input
                value={name}
                onChange={(e) => { setName(e.target.value); setAddError('') }}
                placeholder="Competitor name (e.g. Salesforce)"
              />
              <Input
                value={url}
                onChange={(e) => { setUrl(e.target.value); setAddError('') }}
                placeholder="Website URL (e.g. salesforce.com)"
              />
              <Button
                onClick={handleAdd}
                disabled={addMutation.isPending}
                className="w-full"
              >
                {addMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                {addMutation.isPending ? 'Adding…' : 'Add Competitor'}
              </Button>
            </div>
            {addError && !limitReached && (
              <div className="mt-3 flex items-center gap-2 text-sm text-feedback-error">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {addError}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Competitor drawer */}
      <CompetitorDrawer
        competitor={drawerCompetitor}
        onClose={() => setDrawerCompetitor(null)}
      />

      {/* Competitor limit modal */}
      {limitReached && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => { setLimitReached(false); setAddError('') }}
        >
          <div
            className="relative w-full max-w-sm rounded-xl border border-border bg-surface p-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => { setLimitReached(false); setAddError('') }}
              className="absolute right-3 top-3 rounded-lg p-1 text-text-muted transition-colors hover:bg-surface-overlay"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand-accent/10">
              <Lock className="h-6 w-6 text-brand-accent" />
            </div>
            <h2 className="mb-2 text-lg font-bold text-text">Competitor Limit Reached</h2>
            <p className="mb-6 text-sm text-text-muted">
              You've tracked 20 competitors on your current plan. Upgrade to Enterprise for unlimited competitor tracking, white-label reports, and multi-client management.
            </p>
            <a
              href="mailto:hello@lensora.io?subject=Enterprise Upgrade"
              className="block w-full rounded-lg bg-brand-accent px-4 py-3 text-sm font-semibold text-text-inverse transition-opacity hover:opacity-90"
            >
              Upgrade to Enterprise
            </a>
            <a
              href="mailto:hello@lensora.io"
              className="mt-3 block text-xs text-text-muted hover:text-text"
            >
              Contact Sales →
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
